import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { AlarmClock, Banknote, FileSpreadsheet, Link2, Loader2, Upload } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Switch } from "./ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { api } from "../lib/api";
import { saveBlob } from "../lib/download";

const err = (e, d) => toast.error(e?.response?.data?.detail || d);
const Th = ({ children }) => <th className="px-2 py-2 text-xs font-semibold text-slate-600">{children}</th>;

const LinkTab = () => {
  const ref = useRef(null);
  const [busy, setBusy] = useState("");
  const [res, setRes] = useState(null);
  const dl = async () => {
    setBusy("dl");
    try { await saveBlob(await api.ninjaLinkTemplate(), "ربط_كباتن_نينجا.xlsx"); } catch (e) { err(e, "تعذّر تنزيل القالب"); } finally { setBusy(""); }
  };
  const up = async (f) => {
    if (!f) return;
    setBusy("up");
    try { const r = await api.ninjaLinkImport(f); setRes(r); toast.success(`تم ربط ${r.linked} كابتن`); }
    catch (e) { err(e, "تعذّر رفع الملف"); } finally { setBusy(""); if (ref.current) ref.current.value = ""; }
  };
  return (
    <div className="space-y-3" data-testid="ninja-link-tab">
      <p className="text-sm text-slate-600">نزّل القالب، اكتب رقم الكابتن جنب كل موظف، ثم ارفعه — يتم الربط بالرقم الوظيفي أو رقم الهوية.</p>
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" className="gap-2" onClick={dl} disabled={busy === "dl"} data-testid="ninja-link-template">
          {busy === "dl" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 text-emerald-600" />} تنزيل القالب
        </Button>
        <Button className="gap-2 bg-blue-600 hover:bg-blue-700" onClick={() => ref.current?.click()} disabled={busy === "up"} data-testid="ninja-link-upload">
          {busy === "up" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} رفع الملف المعبّأ
        </Button>
        <input ref={ref} type="file" accept=".xlsx" className="hidden" data-testid="ninja-link-file" onChange={(e) => up(e.target.files?.[0])} />
      </div>
      {res && (
        <div className="rounded-xl border bg-slate-50 p-3 text-sm" data-testid="ninja-link-result">
          <p>تم ربط <b>{res.linked}</b> كابتن.</p>
          {res.not_found_count > 0 && <p className="text-amber-700 mt-1">{res.not_found_count} صف لم يُعثر على موظفه: <span dir="ltr">{res.not_found.join("، ")}</span></p>}
        </div>
      )}
    </div>
  );
};

const TimeTab = () => {
  const [d, setD] = useState(null);
  const [date, setDate] = useState("");
  const [limit, setLimit] = useState("");
  const load = useCallback(async (dt) => {
    try { const r = await api.ninjaTimeAlerts(dt); setD(r); setDate(r.date || ""); setLimit(String(r.limit)); } catch (e) { err(e, "تعذّر التحميل"); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    try { await api.saveNinjaSettings({ order_time_limit: Number(limit) }); toast.success("تم حفظ الحد"); load(date); } catch (e) { err(e, "تعذّر الحفظ"); }
  };
  if (!d) return <Loader2 className="mx-auto my-6 h-5 w-5 animate-spin text-slate-400" />;
  return (
    <div className="space-y-3" data-testid="ninja-time-tab">
      <div className="flex flex-wrap items-end gap-2">
        <label className="text-xs text-slate-600">الحد الأعلى لمتوسط مدة الطلب (دقيقة)
          <Input type="number" min="1" value={limit} onChange={(e) => setLimit(e.target.value)} className="w-28 mt-1" data-testid="ninja-time-limit" />
        </label>
        <Button size="sm" onClick={save} data-testid="ninja-time-save">حفظ</Button>
        <select value={date} onChange={(e) => load(e.target.value)} className="h-9 rounded-md border px-2 text-sm ms-auto" data-testid="ninja-time-date">
          {d.dates.map((x) => <option key={x} value={x}>{x}</option>)}
        </select>
      </div>
      {!d.rows.length ? <p className="py-6 text-center text-sm text-emerald-700" data-testid="ninja-time-empty">لا يوجد كباتن تجاوزوا الحد في هذا اليوم</p> : (
        <div className="overflow-x-auto rounded-xl border">
          <table className="w-full text-sm text-right" data-testid="ninja-time-table">
            <thead className="bg-slate-50"><tr><Th>الكابتن</Th><Th>الموظف</Th><Th>الطلبات</Th><Th>متوسط المدة</Th><Th>فوق الحد</Th></tr></thead>
            <tbody>{d.rows.map((r) => (
              <tr key={r.rider_id} className="border-t" data-testid={`ninja-time-row-${r.rider_id}`}>
                <td className="px-2 py-2 font-mono-nums">{r.rider_id}</td>
                <td className="px-2 py-2">{r.name || <span className="text-slate-400">غير مربوط</span>}</td>
                <td className="px-2 py-2">{r.orders}</td>
                <td className="px-2 py-2 font-bold text-rose-600">{r.avg_min} د</td>
                <td className="px-2 py-2 text-amber-700">+{r.over_by} د</td>
              </tr>))}</tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const CashTab = () => {
  const [d, setD] = useState(null);
  const [month, setMonth] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(async (m) => {
    try { const r = await api.ninjaCash(m); setD(r); setMonth(r.month); } catch (e) { err(e, "تعذّر التحميل"); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const apply = async () => {
    setBusy(true);
    try {
      const r = await api.ninjaCashApply(month);
      toast.success(`تم تطبيق الخصم على ${r.applied} مسير`);
      if (r.skipped_no_salary?.length) toast.warning(`بدون مسير راتب لهذا الشهر: ${r.skipped_no_salary.join("، ")}`);
      load(month);
    } catch (e) { err(e, "تعذّر التطبيق"); } finally { setBusy(false); }
  };
  const toggleAuto = async (v) => {
    try { await api.saveNinjaSettings({ cash_auto_apply: v }); setD({ ...d, auto: v }); toast.success(v ? "سيُطبّق الخصم تلقائياً عند رفع التقارير" : "أُوقف التطبيق التلقائي"); } catch (e) { err(e, "تعذّر الحفظ"); }
  };
  if (!d) return <Loader2 className="mx-auto my-6 h-5 w-5 animate-spin text-slate-400" />;
  return (
    <div className="space-y-3" data-testid="ninja-cash-tab">
      <div className="flex flex-wrap items-center gap-3">
        <Input type="month" value={month} onChange={(e) => load(e.target.value)} className="w-40" data-testid="ninja-cash-month" />
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <Switch checked={!!d.auto} onCheckedChange={toggleAuto} data-testid="ninja-cash-auto" /> تطبيق تلقائي عند رفع التقرير
        </label>
        <Button className="gap-2 bg-rose-600 hover:bg-rose-700 ms-auto" onClick={apply} disabled={busy || !d.rows.length} data-testid="ninja-cash-apply">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} تطبيق على الرواتب ({d.total} ر.س)
        </Button>
      </div>
      {!d.rows.length ? <p className="py-6 text-center text-sm text-slate-500" data-testid="ninja-cash-empty">لا يوجد عجز نقدي لموظفين مربوطين في هذا الشهر</p> : (
        <div className="overflow-x-auto rounded-xl border">
          <table className="w-full text-sm text-right" data-testid="ninja-cash-table">
            <thead className="bg-slate-50"><tr><Th>الموظف</Th><Th>الكابتن</Th><Th>الأيام</Th><Th>العجز</Th><Th>المطبّق في المسير</Th></tr></thead>
            <tbody>{d.rows.map((r) => (
              <tr key={r.employee_id} className="border-t" data-testid={`ninja-cash-row-${r.employee_id}`}>
                <td className="px-2 py-2">{r.name}</td>
                <td className="px-2 py-2 font-mono-nums">{r.rider_id}</td>
                <td className="px-2 py-2 text-xs" dir="ltr">{r.days.map((x) => `${x.date.slice(5)}: ${x.amount}`).join(" · ")}</td>
                <td className="px-2 py-2 font-bold text-rose-600">{r.amount} ر.س</td>
                <td className="px-2 py-2">{!r.has_salary ? <span className="text-amber-600 text-xs">لا يوجد مسير</span> : r.applied === r.amount ? <span className="text-emerald-600 text-xs">مطبّق</span> : <span className="text-xs text-slate-500">{r.applied || 0} ر.س</span>}</td>
              </tr>))}</tbody>
          </table>
        </div>
      )}
      {d.unmatched?.length > 0 && (
        <p className="text-xs text-amber-700" data-testid="ninja-cash-unmatched">كباتن عليهم عجز وغير مربوطين بموظف: {d.unmatched.map((u) => `${u.rider_id} (${u.amount} ر.س)`).join("، ")} — اربطهم من تبويب «ربط الكباتن».</p>
      )}
    </div>
  );
};

export const NinjaTools = () => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button variant="outline" size="sm" className="gap-1.5 border-rose-200 text-rose-700 hover:bg-rose-50" onClick={() => setOpen(true)} data-testid="ninja-tools-button">
        <AlarmClock className="h-4 w-4" /> <span className="hidden sm:inline">أدوات نينجا</span>
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto" dir="rtl" data-testid="ninja-tools-dialog">
          <DialogHeader className="text-start"><DialogTitle className="font-heading">أدوات نينجا</DialogTitle></DialogHeader>
          <Tabs defaultValue="time" dir="rtl">
            <TabsList className="grid grid-cols-3 w-full">
              <TabsTrigger value="time" className="gap-1 text-xs sm:text-sm" data-testid="ninja-tab-time"><AlarmClock className="h-3.5 w-3.5" /> مدة الطلب</TabsTrigger>
              <TabsTrigger value="cash" className="gap-1 text-xs sm:text-sm" data-testid="ninja-tab-cash"><Banknote className="h-3.5 w-3.5" /> العجز النقدي</TabsTrigger>
              <TabsTrigger value="link" className="gap-1 text-xs sm:text-sm" data-testid="ninja-tab-link"><Link2 className="h-3.5 w-3.5" /> ربط الكباتن</TabsTrigger>
            </TabsList>
            <TabsContent value="time" className="mt-4"><TimeTab /></TabsContent>
            <TabsContent value="cash" className="mt-4"><CashTab /></TabsContent>
            <TabsContent value="link" className="mt-4"><LinkTab /></TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
    </>
  );
};
